// Shared constants for Vertex extension
// Used by both vanilla JS and TypeScript/Preact code

// Import build-time environment (generated by build.js)
import { BUILD_ENV, GIT_REV, VERSION } from './build-config.js';

export const CHROME_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/vertex/dhhilgfkipbaabbjdbpkddekhcoipdli';

/**
 * Fetch wrapper that includes credentials and client identification.
 * Use this for all API requests instead of raw fetch().
 */
export function vertexFetch(url, init = {}) {
  const headers = new Headers(init.headers);
  headers.set('X-Vertex-Client', CLIENT_ID);
  return fetch(url, { ...init, headers, credentials: 'include' });
}

export const STORAGE_KEYS = {
  USER_KEY: 'vertexUserKey',
  USER_ALIAS: 'vertexUserAlias',
  ONBOARDING_COMPLETED: 'vertexOnboardingCompleted',
  TIPS_SHOWN: 'vertexTipsShown',
  ENVIRONMENT: 'vertexEnvironment'
};

// Environment configurations
export const ENVIRONMENTS = {
  local: {
    api: 'http://localhost:8080',
    frontend: 'http://localhost:8081'
  },
  dev: {
    api: 'https://devapi.vertexapp.cc',
    frontend: 'https://dev.vertexapp.cc'
  },
  prod: {
    api: 'https://api.vertexapp.io',
    frontend: 'https://vertexapp.io'
  }
};

// Default environment based on context
function getDefaultEnvironment() {
  // In web context (not extension), infer from hostname
  if (typeof window !== 'undefined' && !isExtension) {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'local';
    }
    // Dev environments
    if (hostname === 'dev.vertexapp.cc') {
      return 'dev';
    }
    // Prod environments
    if (hostname === 'vertexapp.io' || hostname === 'vertexapp.cc') {
      return 'prod';
    }
  }
  // For extension, use build-time environment
  return BUILD_ENV;
}

/**
 * Derive API endpoint from current hostname (web context only).
 * - localhost -> localhost:8080
 * - dev.{domain} -> devapi.{domain}
 * - {domain} -> api.{domain}
 */
function deriveApiFromHostname() {
  if (typeof window === 'undefined') return null;

  const { protocol, hostname } = window.location;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:8080`;
  }

  // Dev: dev.vertexapp.cc -> devapi.vertexapp.cc
  if (hostname.startsWith('dev.')) {
    const baseDomain = hostname.slice(4); // Remove 'dev.'
    return `${protocol}//devapi.${baseDomain}`;
  }

  // Prod: vertexapp.cc -> api.vertexapp.cc
  return `${protocol}//api.${hostname}`;
}

// Detect if running in Chrome extension context
const isExtension = typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;

// Client identifier sent with every API request
const CLIENT_NAME = isExtension ? 'Extension' : 'Frontend';
export const CLIENT_ID = `Vertex/${CLIENT_NAME} ${VERSION}+${GIT_REV}`;


/**
 * Get a value from storage (chrome.storage.local or localStorage)
 * @param {string} key - The storage key
 * @returns {Promise<any>} The stored value or null
 */
async function storageGet(key) {
  if (isExtension) {
    const result = await chrome.storage.local.get([key]);
    return result[key] || null;
  }
  const value = localStorage.getItem(key);
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
}

/**
 * Set a value in storage (chrome.storage.local or localStorage)
 * @param {string} key - The storage key
 * @param {any} value - The value to store
 * @returns {Promise<void>}
 */
export async function storageSet(key, value) {
  if (isExtension) {
    await chrome.storage.local.set({ [key]: value });
  } else {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
}

/**
 * Remove a value from storage (chrome.storage.local or localStorage)
 * @param {string} key - The storage key
 * @returns {Promise<void>}
 */
export async function storageRemove(key) {
  if (isExtension) {
    await chrome.storage.local.remove([key]);
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * Get the current environment
 * @returns {Promise<string>} The environment name ('local', 'dev', or 'prod')
 */
export async function getEnvironment() {
  const stored = await storageGet(STORAGE_KEYS.ENVIRONMENT);
  if (stored && ENVIRONMENTS[stored]) {
    return stored;
  }
  return getDefaultEnvironment();
}

/**
 * Set the current environment
 * @param {string} env - The environment name ('local', 'dev', or 'prod')
 * @returns {Promise<void>}
 */
export async function setEnvironment(env) {
  if (ENVIRONMENTS[env]) {
    await storageSet(STORAGE_KEYS.ENVIRONMENT, env);
  }
}

/**
 * Get the API endpoint.
 * - Web context: derived from current hostname
 * - Extension context: from ENVIRONMENTS based on build env
 * @returns {Promise<string>} The API endpoint URL
 */
export async function getApiEndpoint() {
  // Web context: derive from current hostname
  if (!isExtension) {
    const derived = deriveApiFromHostname();
    if (derived) return derived;
  }

  // Extension context: use ENVIRONMENTS based on build env
  const env = await getEnvironment();
  return ENVIRONMENTS[env].api;
}

/**
 * Get the user key from storage
 * @returns {Promise<string|null>} The user key or null if not set
 */
export async function getUserKey() {
  return storageGet(STORAGE_KEYS.USER_KEY);
}

/**
 * Get the user alias from storage
 * @returns {Promise<string|null>} The user alias or null if not set
 */
export async function getUserAlias() {
  return storageGet(STORAGE_KEYS.USER_ALIAS);
}

/**
 * Get the frontend URL.
 * - Web context: current origin
 * - Extension context: from ENVIRONMENTS based on build env
 * @returns {Promise<string>} The frontend URL
 */
export async function getFrontendUrl() {
  // Web context: use current origin
  if (!isExtension && typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Extension context: use ENVIRONMENTS
  const env = await getEnvironment();
  return ENVIRONMENTS[env].frontend;
}
